import { useState, useEffect, useRef } from "react";
import type Hls from "hls.js";
import { useScrollTarget } from "@/lib/simple-router";
import EnhancedBookingWidget from "@/components/enhanced-booking-widget";
import Footer from "@/components/footer";
import VillaListing from "@/components/villa-listing";
const CLOUDFLARE_STREAM_VIDEO_ID = import.meta.env.VITE_CLOUDFLARE_STREAM_VIDEO_ID || "5bebb0b893b4031aac9dca8e13cff1b9";
const CLOUDFLARE_STREAM_CUSTOMER = import.meta.env.VITE_CLOUDFLARE_STREAM_CUSTOMER || "c9ht3kqbr7zgtj10";

function HeroVideo({ hlsUrl, posterUrl, posterUrlMobile }: { hlsUrl: string; posterUrl: string; posterUrlMobile?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  
  // Synchronously detect mobile on first render to ensure correct poster is requested immediately
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  const activePosterUrl = (isMobile && posterUrlMobile) ? posterUrlMobile : posterUrl;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || videoFailed) return;

    let mounted = true;
    let timeout: ReturnType<typeof setTimeout>;
    let idleHandle: number | null = null;
    let rafHandle: number | null = null;
    let rafHandle2: number | null = null;
    let usedIdleCallback = false;

    // Lazy-load hls.js only when needed
    const initHls = async () => {
      try {
        const HlsModule = await import('hls.js');
        const Hls = HlsModule.default;
        
        if (!mounted) return;

        if (Hls.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: 90,
            xhrSetup: (xhr: XMLHttpRequest) => {
              xhr.withCredentials = false;
            }
          });
          hlsRef.current = hls;
          
          hls.loadSource(hlsUrl);
          hls.attachMedia(video);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            clearTimeout(timeout);
            video.play().catch(() => {});
          });

          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal) {
              clearTimeout(timeout);
              // Silently fall back to poster - no console error
              setVideoFailed(true);
              hls.destroy();
            }
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari native HLS support - doesn't need hls.js
          video.src = hlsUrl;
          const onLoadedMetadata = () => {
            clearTimeout(timeout);
            video.play().catch(() => {});
          };
          const onError = () => {
            clearTimeout(timeout);
            setVideoFailed(true);
          };
          video.addEventListener('loadedmetadata', onLoadedMetadata);
          video.addEventListener('error', onError);
          
          // Store refs for cleanup
          (video as any)._hlsCleanup = { onLoadedMetadata, onError };
        } else {
          // No HLS support - fall back to poster
          setVideoFailed(true);
        }
      } catch {
        // Failed to load hls.js - fall back to poster
        if (mounted) setVideoFailed(true);
      }
    };

    const startPlayer = () => {
      if (!mounted) return;
      // Set a timeout - if video doesn't start in 5 seconds, just show poster
      timeout = setTimeout(() => {
        if (video.readyState < 3) {
          setVideoFailed(true);
        }
      }, 5000);
      initHls();
    };

    // Defer player startup until after first paint so hls.js and the video
    // manifest/stream requests stay out of the initial critical path.
    // Double rAF = after the first frame has painted; then wait for idle.
    rafHandle = requestAnimationFrame(() => {
      rafHandle2 = requestAnimationFrame(() => {
        if ('requestIdleCallback' in window) {
          usedIdleCallback = true;
          idleHandle = (window as any).requestIdleCallback(startPlayer, { timeout: 1500 });
        } else {
          idleHandle = globalThis.setTimeout(startPlayer, 200) as unknown as number;
        }
      });
    });

    return () => {
      mounted = false;
      clearTimeout(timeout);
      if (rafHandle !== null) cancelAnimationFrame(rafHandle);
      if (rafHandle2 !== null) cancelAnimationFrame(rafHandle2);
      if (idleHandle !== null) {
        if (usedIdleCallback) (window as any).cancelIdleCallback(idleHandle);
        else clearTimeout(idleHandle);
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      // Clean up Safari event listeners
      if ((video as any)?._hlsCleanup) {
        video.removeEventListener('loadedmetadata', (video as any)._hlsCleanup.onLoadedMetadata);
        video.removeEventListener('error', (video as any)._hlsCleanup.onError);
        delete (video as any)._hlsCleanup;
      }
    };
  }, [hlsUrl, videoFailed]);

  // If video failed to load, just show the poster image (responsive)
  if (videoFailed) {
    return (
      <picture>
        {posterUrlMobile && (
          <source media="(max-width: 767px)" srcSet={posterUrlMobile} type="image/webp" />
        )}
        <img
          src={posterUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
      </picture>
    );
  }

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      loop
      playsInline
      poster={activePosterUrl}
      className="absolute inset-0 w-full h-full object-cover"
      aria-hidden="true"
    />
  );
}

export default function Home() {
  // Get scroll target from router (set by footer links from other pages)
  const [scrollTarget, clearScrollTarget] = useScrollTarget();

  // Handle scroll to section when scrollTarget changes
  useEffect(() => {
    if (!scrollTarget) return;

    // Special case: scroll to top of page
    if (scrollTarget === 'top') {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        clearScrollTarget();
      }, 50);
      return;
    }

    const scrollToSection = (elementId: string) => {
      const headerOffset = 100;
      let attempts = 0;
      const maxAttempts = 30;
      
      const tryScroll = () => {
        const element = document.getElementById(elementId);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'auto'
          });
          
          // Clear the scroll target after scrolling
          clearScrollTarget();
        } else if (attempts < maxAttempts) {
          attempts++;
          requestAnimationFrame(tryScroll);
        }
      };
      
      // Wait a bit for the page to render before scrolling
      setTimeout(tryScroll, 100);
    };

    scrollToSection(scrollTarget);
  }, [scrollTarget, clearScrollTarget]);

  // Handle hash fragment scrolling on initial page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const elementId = hash.replace('#', '');
      const headerOffset = 100;
      
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'auto' });
        }
      }, 300);
    }
  }, []);
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="hero" className="relative h-screen overflow-hidden">
        <div className="absolute inset-0 hero-video-container">
          {/* HLS Video via Cloudflare Stream */}
          <HeroVideo 
            hlsUrl={`https://customer-${CLOUDFLARE_STREAM_CUSTOMER}.cloudflarestream.com/${CLOUDFLARE_STREAM_VIDEO_ID}/manifest/video.m3u8`}
            posterUrl="/hero-poster.webp?v=2"
            posterUrlMobile="/hero-poster-mobile.webp?v=2"
          />
        </div>
        
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="text-center text-white max-w-4xl mx-auto px-4 sm:px-6">
            <div aria-hidden="true" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-4 sm:mb-6 leading-tight animate-fade-in-up text-transparent" style={{ fontFamily: 'Lato', fontWeight: '900' }}>
              Luxury Lake View <br className="hidden sm:block" />
              <span className="block sm:inline text-transparent">Accommodation</span>
            </div>
            <p className="sm:text-xl md:text-2xl mb-6 sm:mb-8 animate-fade-in-up max-w-3xl mx-auto text-transparent text-[48px] font-light" style={{ fontFamily: 'Lato', fontWeight: '300' }}>SELECT YOUR DATES</p>
            
            <EnhancedBookingWidget variant="hero" />
          </div>
        </div>
      </section>

      <VillaListing />
      <Footer />
    </div>
  );
}
